// Shared data store using localStorage + custom events for cross-dashboard sync

export interface Officer {
  id: string;
  name: string;
  psira: string;
  phone?: string;
  email?: string;
  site: string;
  siteId: string;
  status: "live" | "offline";
  coverage: number;
  checkins: number;
  lat: number;
  lng: number;
}

export interface Site {
  id: string;
  name: string;
  address: string;
  officers: string[];
  checkpoints: number;
}

export interface LateReport {
  id: string;
  officerId: string;
  officerName: string;
  checkpoint: string;
  site: string;
  reason: string;
  details: string;
  lateMinutes: number;
  timestamp: string; // ISO
}

export interface ShiftPassword {
  officerId: string;
  password: string;
  issuedAt: string; // ISO timestamp
  expiresAt: string; // ISO timestamp
  issuedBy: string;
}

export interface AutoPasswordSchedule {
  enabled: boolean;
  time: string; // HH:mm format
  expiryHours: number;
}

const OFFICERS_KEY = "sftrack_officers";
const SITES_KEY = "sftrack_sites";
const PASSWORDS_KEY = "sftrack_shift_passwords";
const AUTO_SCHEDULE_KEY = "sftrack_auto_password_schedule";
const LATE_REPORTS_KEY = "sftrack_late_reports";

const defaultOfficers: Officer[] = [
  { id: "1", name: "Sipho Nkosi", psira: "PSI-2847391", site: "Riverside Office Park", siteId: "1", status: "live", coverage: 75, checkins: 3, lat: -29.8838, lng: 30.9462 },
  { id: "2", name: "Thabo Moyo", psira: "PSI-1923847", site: "Riverside Office Park", siteId: "1", status: "live", coverage: 50, checkins: 2, lat: -29.8845, lng: 30.9470 },
  { id: "3", name: "Lebo Dlamini", psira: "PSI-3847291", site: "Sandton Business Centre", siteId: "2", status: "offline", coverage: 0, checkins: 0, lat: -26.1076, lng: 28.0567 },
  { id: "4", name: "Grace Molefe", psira: "PSI-4729183", site: "Sandton Business Centre", siteId: "2", status: "live", coverage: 25, checkins: 1, lat: -26.1080, lng: 28.0572 },
];

const defaultSites: Site[] = [
  { id: "1", name: "Riverside Office Park", address: "12 River Rd, Durban", officers: ["Sipho Nkosi", "Thabo Moyo"], checkpoints: 4 },
  { id: "2", name: "Sandton Business Centre", address: "45 Sandton Dr, Johannesburg", officers: ["Lebo Dlamini", "Grace Molefe"], checkpoints: 3 },
];
const defaultLateReports: LateReport[] = [
  { id: "lr1", officerId: "4", officerName: "Grace Molefe", checkpoint: "Executive Wing", site: "Sandton Business Centre", reason: "Traffic / transport delay", details: "Taxi was delayed due to road closure on Sandton Drive", lateMinutes: 15, timestamp: new Date().toISOString() },
];

const CHANGE_EVENT = "sftrack_data_change";

export function getOfficers(): Officer[] {
  try {
    const stored = localStorage.getItem(OFFICERS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return defaultOfficers;
}

export function getSites(): Site[] {
  try {
    const stored = localStorage.getItem(SITES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return defaultSites;
}

export function setOfficers(officers: Officer[]) {
  localStorage.setItem(OFFICERS_KEY, JSON.stringify(officers));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { type: "officers" } }));
}

export function setSites(sites: Site[]) {
  localStorage.setItem(SITES_KEY, JSON.stringify(sites));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { type: "sites" } }));
}

export function addOfficer(officer: Officer) {
  const officers = getOfficers();
  officers.push(officer);
  setOfficers(officers);
  const sites = getSites();
  const updated = sites.map(s => s.id === officer.siteId ? { ...s, officers: [...s.officers, officer.name] } : s);
  setSites(updated);
}

export function removeOfficer(officerId: string) {
  const officers = getOfficers();
  const officer = officers.find(o => o.id === officerId);
  const filtered = officers.filter(o => o.id !== officerId);
  setOfficers(filtered);
  if (officer) {
    const sites = getSites();
    const updated = sites.map(s => s.id === officer.siteId ? { ...s, officers: s.officers.filter(n => n !== officer.name) } : s);
    setSites(updated);
  }
}

export function addSite(site: Site) {
  const sites = getSites();
  sites.push(site);
  setSites(sites);
}

export function updateOfficer(officerId: string, updates: Partial<Officer>) {
  const officers = getOfficers();
  const oldOfficer = officers.find(o => o.id === officerId);
  if (!oldOfficer) return;
  const newOfficer = { ...oldOfficer, ...updates };
  const updated = officers.map(o => o.id === officerId ? newOfficer : o);
  setOfficers(updated);
  // Update site assignments if site changed
  if (updates.siteId && updates.siteId !== oldOfficer.siteId) {
    const sites = getSites();
    const result = sites.map(s => {
      if (s.id === oldOfficer.siteId) return { ...s, officers: s.officers.filter(n => n !== oldOfficer.name) };
      if (s.id === newOfficer.siteId) return { ...s, officers: [...s.officers, newOfficer.name] };
      return s;
    });
    setSites(result);
  } else if (updates.name && updates.name !== oldOfficer.name) {
    const sites = getSites();
    const result = sites.map(s => s.id === oldOfficer.siteId ? { ...s, officers: s.officers.map(n => n === oldOfficer.name ? newOfficer.name : n) } : s);
    setSites(result);
  }
}

export function removeSite(siteId: string) {
  const sites = getSites().filter(s => s.id !== siteId);
  setSites(sites);
}

// ========== SHIFT PASSWORD SYSTEM ==========

export function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let pwd = "";
  for (let i = 0; i < 8; i++) {
    pwd += chars[Math.floor(Math.random() * chars.length)];
    if (i === 3) pwd += "-"; // format: XXXX-XXXX
  }
  return pwd;
}

export function getShiftPasswords(): Record<string, ShiftPassword> {
  try {
    const stored = localStorage.getItem(PASSWORDS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

export function issueShiftPassword(officerId: string, expiryHours: number = 12, issuedBy: string = "Security Operator"): ShiftPassword {
  const passwords = getShiftPasswords();
  const now = new Date();
  const expires = new Date(now.getTime() + expiryHours * 60 * 60 * 1000);
  const entry: ShiftPassword = {
    officerId,
    password: generatePassword(),
    issuedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
    issuedBy,
  };
  passwords[officerId] = entry;
  localStorage.setItem(PASSWORDS_KEY, JSON.stringify(passwords));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { type: "passwords" } }));
  return entry;
}

export function getOfficerPassword(officerId: string): ShiftPassword | null {
  const passwords = getShiftPasswords();
  const entry = passwords[officerId];
  if (!entry) return null;
  // Check if expired
  if (new Date(entry.expiresAt) < new Date()) return null;
  return entry;
}

export function issueAllPasswords(expiryHours: number = 12): void {
  const officers = getOfficers();
  officers.forEach(o => issueShiftPassword(o.id, expiryHours));
}

export function getAutoPasswordSchedule(): AutoPasswordSchedule {
  try {
    const stored = localStorage.getItem(AUTO_SCHEDULE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return { enabled: false, time: "06:00", expiryHours: 12 };
}

export function setAutoPasswordSchedule(schedule: AutoPasswordSchedule) {
  localStorage.setItem(AUTO_SCHEDULE_KEY, JSON.stringify(schedule));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { type: "schedule" } }));
}

// ========== LATE CHECK-IN REPORTS ==========

export function getLateReports(): LateReport[] {
  try {
    const stored = localStorage.getItem(LATE_REPORTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return defaultLateReports;
}

export function addLateReport(report: LateReport) {
  const reports = getLateReports();
  reports.unshift(report);
  localStorage.setItem(LATE_REPORTS_KEY, JSON.stringify(reports));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { type: "late_reports" } }));
}

// Hook helper: subscribe to changes
export function onDataChange(callback: () => void) {
  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}
